import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import ScrollToTop from './components/ScrollToTop'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { HelmetProvider } from 'react-helmet-async'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { PlatformProvider } from './contexts/PlatformContext'
import { LanguageProvider } from './contexts/LanguageContext'
import { OfflineProvider } from './contexts/OfflineContext'
import { GlobalPrefetchProvider } from './contexts/GlobalPrefetchContext'
import { Toaster } from 'sonner'
import Login from './pages/Login'
import AdminModule from './modules/admin/Admin.container'
import Admin from './pages/Admin'
import UrjaSanyojak from './pages/UrjaSanyojak'
import Dashboard from './pages/Dashboard'
import ExecutiveDashboard from './pages/ExecutiveDashboard'
import GaushalaAccessWrapper from './pages/gaushala/GaushalaAccessWrapper'
import BiogasSangh from './pages/BiogasSangh'
import SalesAccessWrapper from './modules/sales/SalesAccessWrapper'
import { PurificationModule } from './modules/purification'
import ManufacturingAccessWrapper from './modules/manufacturing/ManufacturingAccessWrapper'
import { ExpiringContractsPage } from './features/contracts/expiring'
import UrjaNeta from './pages/UrjaNeta'
import TransportAccessWrapper from './modules/transporter/TransportAccessWrapper'
import MarketplaceModule from './modules/marketplace/Marketplace.container'
import PublicMarketplaceModule from './modules/marketplace/PublicMarketplace.container'
import RoleBasedRouter from './components/routing/RoleBasedRouter'
import RazorpayDemo from './pages/RazorpayDemo'
import Transport from './pages/Transport'
import DriverRoutesPage from './pages/transport/DriverRoutesPage'
import PWAUpdatePrompt from './components/pwa/PWAUpdatePrompt'
import { GlobalPrefetchBanner } from './components/prefetch/GlobalPrefetchBanner'
import { useGlobalPrefetch } from './contexts/GlobalPrefetchContext'

// Inventory Batch Pages (US-BATCH-001)
import CreateBatchPage from './pages/inventory/batches/CreateBatchPage'
import BatchListPage from './pages/inventory/batches/BatchListPage'
import BatchDetailPage from './pages/inventory/batches/BatchDetailPage'

// Customer Orders Pages (US-ORD-007)
import CustomerOrdersPage from './pages/commerce/customer-orders'

// Invoice Pages (US-INV-002)
import { InvoiceDetailsPage } from './pages/invoices/InvoiceDetailsPage'
import { InvoicesListPage } from './pages/invoices'
import { DraftInvoicePage } from './pages/invoices/DraftInvoicePage'

// Overdue Invoice Pages (US-INV-004)
import { OverdueInvoicesPage } from './pages/urjavyapar/OverdueInvoicesPage'
import { CollectionReportPage } from './pages/urjavyapar/CollectionReportPage'

// Delivery Pages
import NewDeliveryRequest from './pages/commerce/delivery/NewDeliveryRequest'


// Landing pages
import LandingLayout from './components/layouts/LandingLayout'
import AdminContentLayout from './components/layouts/AdminContentLayout'
import Home from './pages/landing/Home'
import Benefits from './pages/landing/Benefits'
import About from './pages/landing/About'
import Users from './pages/landing/Users'
import Contact from './pages/landing/Contact'
import SuccessStories from './pages/landing/SuccessStories'
import FAQ from './pages/landing/FAQ'
import NotFound from './pages/landing/NotFound'
import MarketplaceLanding from './pages/public/MarketplaceLanding'
import OrderApprovalPage from './pages/public/OrderApprovalPage'

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth()

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-emerald-600 border-t-transparent" />
      </div>
    )
  }

  return user ? <>{children}</> : <Navigate to="/login" />
}

/**
 * App Routes Content with Prefetch Banner
 */
function AppRoutesWithBanner() {
  const prefetch = useGlobalPrefetch()
  const { user } = useAuth()

  // Block only GAUSHALA users during initial offline data prefetch
  // Other roles (farmers, sales, transport, etc.) don't need offline mode
  const isGaushalaUser = user?.appType === 'gausakhi'

  if (prefetch.isBlocking && isGaushalaUser) {
    return (
      <div className="fixed inset-0 bg-white z-[9999] flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="mb-6">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-green-600 border-t-transparent mx-auto"></div>
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Initializing Offline Mode</h2>
          <p className="text-gray-600 text-sm mb-4">
            {prefetch.currentTask || 'Preparing your data for offline access...'}
          </p>
          <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
            <div
              className="bg-green-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${prefetch.progress}%` }}
            ></div>
          </div>
          <p className="text-xs text-gray-500">
            {prefetch.progress}% Complete
          </p>
          {prefetch.error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
              {prefetch.error}
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <>
      <AppRoutesContent />
      <GlobalPrefetchBanner
        isLoading={prefetch.isLoading}
        progress={prefetch.progress}
        currentTask={prefetch.currentTask}
        error={prefetch.error}
        isComplete={prefetch.isComplete}
      />
    </>
  )
}

/**
 * Main Routes
 */
function AppRoutesContent() {
  return (
    <Routes>
      {/* Public Landing Pages */}
      <Route element={<LandingLayout />}>
        <Route index element={<Home />} />
        <Route path="benefits" element={<Benefits />} />
        <Route path="about" element={<About />} />
        <Route path="user-types" element={<Users />} />
        <Route path="contact" element={<Contact />} />
        <Route path="success-stories" element={<SuccessStories />} />
        <Route path="faq" element={<FAQ />} />
      </Route>

      {/* Public Auth Route */}
      <Route path="/login" element={<Login />} />

      {/* Razorpay Demo Route (Public for testing) */}
      <Route path="/razorpay-demo" element={<RazorpayDemo />} />

      {/* Public Draft Invoice Route (No auth required - customer access via email link) */}
      <Route path="/invoices/draft/:token" element={<DraftInvoicePage />} />

      {/* Public Order Approval Route (No auth required - customer access via email link) */}
      <Route path="/orders/approve" element={<OrderApprovalPage />} />

      {/* Public Marketplace Routes - Story 7.1: Create Marketplace Homepage */}
      <Route path="/marketplace/landing" element={<MarketplaceLanding />} />
      <Route path="/public/marketplace/*" element={<PublicMarketplaceModule />} />
      <Route path="/marketplace/home" element={<PublicMarketplaceModule />} />

      {/* Protected Dashboard Route - Role-based redirect */}
      <Route path="/dashboard" element={
        <PrivateRoute>
          <RoleBasedRouter />
        </PrivateRoute>
      } />

      {/* Protected Admin Routes */}
      <Route path="/admin/*" element={
        <PrivateRoute>
          <AdminModule />
        </PrivateRoute>
      } />
      <Route path="/gaushala/*" element={
        <PrivateRoute>
          <GaushalaAccessWrapper />
        </PrivateRoute>
      } />
      <Route path="/cluster/*" element={
        <PrivateRoute>
          <BiogasSangh />
        </PrivateRoute>
      } />
      <Route path="/sales/*" element={
        <PrivateRoute>
          <SalesAccessWrapper />
        </PrivateRoute>
      } />
      <Route path="/my-orders" element={
        <PrivateRoute>
          <CustomerOrdersPage />
        </PrivateRoute>
      } />
      <Route path="/contracts/expiring" element={
        <PrivateRoute>
          <ExpiringContractsPage />
        </PrivateRoute>
      } />

      {/* Inventory Batch Routes (US-BATCH-001) */}
      <Route path="/inventory/batches" element={
        <PrivateRoute>
          <BatchListPage />
        </PrivateRoute>
      } />
      <Route path="/inventory/batches/create" element={
        <PrivateRoute>
          <CreateBatchPage />
        </PrivateRoute>
      } />
      <Route path="/inventory/batches/:id" element={
        <PrivateRoute>
          <BatchDetailPage />
        </PrivateRoute>
      } />

      {/* Invoice Routes (US-INV-002) */}
      <Route path="/invoices" element={
        <PrivateRoute>
          <InvoicesListPage />
        </PrivateRoute>
      } />
      <Route path="/invoices/:id/details" element={
        <PrivateRoute>
          <InvoiceDetailsPage />
        </PrivateRoute>
      } />
      <Route path="/invoices/:id" element={
        <PrivateRoute>
          <InvoiceDetailsPage />
        </PrivateRoute>
      } />

      {/* Delivery Routes */}
      <Route path="/delivery/new" element={
        <PrivateRoute>
          <NewDeliveryRequest />
        </PrivateRoute>
      } />

      {/* Overdue Invoice Routes (US-INV-004) */}
      <Route path="/urjavyapar/overdue-invoices" element={
        <PrivateRoute>
          <OverdueInvoicesPage />
        </PrivateRoute>
      } />
      <Route path="/urjavyapar/collection-report" element={
        <PrivateRoute>
          <CollectionReportPage />
        </PrivateRoute>
      } />

      <Route path="/purification/*" element={
        <PrivateRoute>
          <PurificationModule />
        </PrivateRoute>
      } />
      <Route path="/urjaneta/*" element={
        <PrivateRoute>
          <UrjaNeta />
        </PrivateRoute>
      } />
      <Route path="/urjasanyojak/*" element={
        <PrivateRoute>
          <UrjaSanyojak />
        </PrivateRoute>
      } />
      <Route path="/admin-dashboard/*" element={
        <PrivateRoute>
          <Admin />
        </PrivateRoute>
      } />
      <Route path="/executive/*" element={
        <PrivateRoute>
          <ExecutiveDashboard />
        </PrivateRoute>
      } />
      <Route path="/transport/*" element={
        <PrivateRoute>
          <TransportAccessWrapper />
        </PrivateRoute>
      } />
      <Route path="/raw-material-transport/*" element={
        <PrivateRoute>
          <Transport />
        </PrivateRoute>
      } />
      <Route path="/transport/driver/routes" element={
        <PrivateRoute>
          <DriverRoutesPage />
        </PrivateRoute>
      } />

      {/* Manufacturing Module Routes - Epics 1-6 */}
      <Route path="/manufacturing/*" element={
        <PrivateRoute>
          <ManufacturingAccessWrapper />
        </PrivateRoute>
      } />

      {/* Marketplace Routes - Story 6.1-6.4: Seller Dashboard & Product Management */}
      {/* Note: My Products and Inquiries have been integrated into Sales Dashboard */}
      {/* Keeping legacy routes for backward compatibility - redirects to sales */}
      <Route path="/marketplace/seller/products" element={
        <PrivateRoute>
          <Navigate to="/sales/my-products" replace />
        </PrivateRoute>
      } />
      <Route path="/marketplace/seller/inquiries" element={
        <PrivateRoute>
          <Navigate to="/sales/inquiries" replace />
        </PrivateRoute>
      } />
      <Route path="/marketplace/*" element={
        <PrivateRoute>
          <MarketplaceModule />
        </PrivateRoute>
      } />

      {/* Protected Landing Pages for Admin Access */}
      <Route path="/content" element={
        <PrivateRoute>
          <AdminContentLayout />
        </PrivateRoute>
      }>
        <Route path="home" element={<Home />} />
        <Route path="benefits" element={<Benefits />} />
        <Route path="about" element={<About />} />
        <Route path="users" element={<Users />} />
        <Route path="contact" element={<Contact />} />
        <Route path="success-stories" element={<SuccessStories />} />
        <Route path="faq" element={<FAQ />} />
      </Route>

      {/* 404 Catch-all */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  )
}

// Create QueryClient instance with default options
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 30, // 30 seconds
      gcTime: 1000 * 60 * 5, // 5 minutes (previously cacheTime)
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
})

function App() {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <LanguageProvider>
          <AuthProvider>
            <OfflineProvider>
              <GlobalPrefetchProvider>
                <PlatformProvider>
                  <Router>
                    <ScrollToTop />
                    <AppRoutesWithBanner />
                    <Toaster position="top-right" richColors closeButton />
                    <PWAUpdatePrompt />
                  </Router>
                </PlatformProvider>
              </GlobalPrefetchProvider>
            </OfflineProvider>
          </AuthProvider>
        </LanguageProvider>
      </QueryClientProvider>
    </HelmetProvider>
  )
}

export default App
